type VersionPipelineKey = "has_script_breakdown" | "has_character_breakdown" | "has_coverage";

/**
 * Matches backend merging of version settings over project / hub pipeline flags.
 * Handles optional `{ data: { … } }` nesting from API wrappers when present.
 */
function extractVersionPipelineFlag(raw: unknown, key: VersionPipelineKey): boolean | undefined {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  if (typeof raw !== "object") {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  if (typeof o[key] === "boolean") {
    return o[key];
  }
  const settings = o.settings;
  if (
    settings &&
    typeof settings === "object" &&
    typeof (settings as Record<string, unknown>)[key] === "boolean"
  ) {
    return (settings as Record<string, boolean>)[key];
  }
  const nested = o.data;
  if (
    nested &&
    typeof nested === "object" &&
    typeof (nested as Record<string, unknown>)[key] === "boolean"
  ) {
    return (nested as Record<string, boolean>)[key];
  }
  return undefined;
}

function effectiveHasPipelineForRun(args: {
  projectHasPipeline: boolean;
  versionFlagKey: VersionPipelineKey;
  versionPayload?: unknown;
}): boolean {
  const versionFlag = extractVersionPipelineFlag(args.versionPayload, args.versionFlagKey);
  if (versionFlag === true) return true;
  if (versionFlag === false) {
    return args.projectHasPipeline;
  }
  return args.projectHasPipeline === true;
}

/** Whether script-breakdown AI runs are enabled (hub visibility + version settings). */
export function effectiveHasScriptBreakdownForRun(args: {
  projectHasScriptBreakdown: boolean;
  versionPayload?: unknown;
  scriptHasMinimalRunContent?: boolean;
}): boolean {
  return effectiveHasPipelineForRun({
    projectHasPipeline: args.projectHasScriptBreakdown,
    versionFlagKey: "has_script_breakdown",
    versionPayload: args.versionPayload,
  });
}

/** Whether character-breakdown AI runs are enabled (hub visibility + version settings). */
export function effectiveHasCharacterBreakdownForRun(args: {
  projectHasCharacterBreakdown: boolean;
  versionPayload?: unknown;
}): boolean {
  return effectiveHasPipelineForRun({
    projectHasPipeline: args.projectHasCharacterBreakdown,
    versionFlagKey: "has_character_breakdown",
    versionPayload: args.versionPayload,
  });
}

/** Whether coverage AI runs are enabled (hub visibility + version settings). */
export function effectiveHasCoverageForRun(args: {
  projectHasCoverage: boolean;
  versionPayload?: unknown;
}): boolean {
  return effectiveHasPipelineForRun({
    projectHasPipeline: args.projectHasCoverage,
    versionFlagKey: "has_coverage",
    versionPayload: args.versionPayload,
  });
}
